'use client'

import { motion } from 'framer-motion'
import { Phone, Mail, MapPin, MessageCircle, Clock, Send, CheckCircle2, AlertCircle, ArrowRight, Navigation, ExternalLink, Zap } from 'lucide-react'
import contactData from '@/data/contact.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { useState } from 'react'
import { WhatsAppIcon } from './WhatsAppIcon'
import { getWhatsAppUrl, PHONE_CALL_URL, WHATSAPP_PHONE } from '@/lib/whatsapp'

const officeHoursTable = [
  { days: 'Sunday', hours: 'Closed', status: 'closed' },
  { days: 'Monday – Friday', hours: '9:00 AM – 6:00 PM', status: 'open' },
  { days: 'Saturday', hours: '9:00 AM – 6:00 PM', status: 'open' },
]

const popularServiceChips = [
  'Power of Attorney',
  'POA Notarization',
  'Golden Visa',
  'Document Attestation',
  'True Copy Attestation',
  'Company Formation',
  'Visa Processing',
]

export function ContactSection() {
  const { ref, isVisible } = useScrollAnimation()
  type SubmissionStatus = 'idle' | 'sending' | 'success' | 'error'

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    service: 'Power of Attorney',
    message: '',
  })
  const [status, setStatus] = useState<SubmissionStatus>('idle')
  const [statusMessage, setStatusMessage] = useState('')

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleChipSelect = (serviceName: string) => {
    setFormData((prev) => ({ ...prev, service: serviceName }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!formData.fullName.trim() || !formData.phone.trim()) {
      setStatus('error')
      setStatusMessage('Please enter your full name and phone number.')
      return
    }

    setStatus('sending')
    setStatusMessage('')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      const result = await res.json()

      if (res.ok) {
        setStatus('success')
        setStatusMessage(result.message || "Message sent! Our specialist will reply within minutes.")
        setFormData({ fullName: '', phone: '', email: '', service: 'Power of Attorney', message: '' })
      } else {
        setStatus('error')
        setStatusMessage(result.message || 'Something went wrong. Please connect with us directly on WhatsApp.')
      }
    } catch {
      setStatus('error')
      setStatusMessage('Network issue occurred. You can click below to message us directly on WhatsApp instead.')
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  }

  const fastWhatsAppUrl = getWhatsAppUrl({
    type: 'consultation',
    service: formData.service || 'Document Clearing',
  })

  const fallbackWhatsAppUrl = getWhatsAppUrl({
    type: 'formFallback',
    name: formData.fullName,
    phone: formData.phone,
    service: formData.service,
    customMessage: formData.message,
  })

  const quickContacts = [
    {
      icon: WhatsAppIcon,
      label: 'WhatsApp (Instant)',
      value: WHATSAPP_PHONE,
      href: fastWhatsAppUrl,
      color: '#FFFFFF',
      bg: '#25D366',
      badge: 'Fastest Response',
      isPrimary: true,
      id: 'contact-quick-wa-btn',
    },
    {
      icon: Phone,
      label: 'Direct Phone',
      value: contactData.contact.phone,
      href: PHONE_CALL_URL,
      color: '#1D4ED8',
      bg: '#EFF6FF',
      badge: null,
      isPrimary: false,
      id: 'contact-quick-call-btn',
    },
    {
      icon: Mail,
      label: 'Email',
      value: contactData.contact.email,
      href: `mailto:${contactData.contact.email}`,
      color: '#D97706',
      bg: '#FEF3C7',
      badge: null,
      isPrimary: false,
      id: 'contact-quick-email-btn',
    },
  ]

  return (
    <section id="contact" ref={ref} className="py-16 md:py-24 bg-[#F8FBFF]">
      <div className="container-narrow">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="section-badge">
            <MessageCircle className="w-4 h-4" />
            Contact Us
          </span>
          <h2 className="section-title">Get Free Expert Consultation</h2>
          <p className="section-subtitle max-w-xl mx-auto">
            Speak with an authorized UAE document clearing specialist. Reach out via WhatsApp for immediate replies or submit the quick form below.
          </p>
        </motion.div>

        {/* Quick Contact Pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap gap-3 justify-center mb-10"
        >
          {quickContacts.map((c, idx) => {
            const Icon = c.icon
            return (
              <a
                key={idx}
                id={c.id}
                href={c.href}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-full border transition-all duration-200 group ${
                  c.isPrimary
                    ? 'bg-[#25D366] text-white border-[#20ba5a] shadow-md hover:brightness-105'
                    : 'bg-white border-[#DBEAFE] hover:-translate-y-0.5'
                }`}
                style={{ boxShadow: c.isPrimary ? '0 4px 14px rgba(37, 211, 102, 0.35)' : '0 2px 8px rgba(29,78,216,0.07)' }}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200"
                  style={{ background: c.isPrimary ? 'rgba(255,255,255,0.2)' : c.bg, color: c.color }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className={`text-[10px] font-semibold uppercase tracking-wider ${c.isPrimary ? 'text-white/90' : 'text-[#94A3B8]'}`}>
                      {c.label}
                    </p>
                    {c.badge && (
                      <span className="text-[9px] bg-white text-[#059669] px-1.5 py-0.2 rounded-full font-bold">
                        {c.badge}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs font-bold ${c.isPrimary ? 'text-white' : 'text-[#1E293B]'}`}>{c.value}</p>
                </div>
              </a>
            )
          })}
        </motion.div>

        {/* Main Grid: Simplified Form + Info */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          {/* Contact Form Card (Spans 7 cols on lg) */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? 'visible' : 'hidden'}
            className="lg:col-span-7"
          >
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-3xl p-6 sm:p-8 border border-[#DBEAFE] shadow-sm"
              style={{ boxShadow: '0 8px 30px rgba(29,78,216,0.09)' }}
            >
              {/* 1-Tap WhatsApp Fast-Option Banner */}
              <div className="mb-6 p-4 rounded-2xl bg-[#DCFCE7] border border-[#86EFAC] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-[#25D366] text-white flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5 sm:mt-0">
                    <WhatsAppIcon className="w-4 h-4 fill-white" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-[#065F46] flex items-center gap-1.5">
                      Need an immediate answer?
                      <span className="inline-flex items-center text-[10px] bg-[#059669] text-white px-2 py-0.5 rounded-full">
                        ~5 Min Reply
                      </span>
                    </h4>
                    <p className="text-xs text-[#047857] mt-0.5">
                      Skip the form & chat directly with our advisor on WhatsApp.
                    </p>
                  </div>
                </div>

                <a
                  id="contact-fast-whatsapp-btn"
                  href={fastWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-white font-bold text-xs shadow-md transition-all duration-200 hover:brightness-105 active:scale-95 flex-shrink-0"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <WhatsAppIcon className="w-3.5 h-3.5 fill-white" />
                  <span>Chat on WhatsApp</span>
                </a>
              </div>

              {/* Form Title */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-[#1E293B] flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#1D4ED8]" />
                  Request Free Callback
                </h3>
                <span className="text-xs text-[#059669] font-semibold bg-[#D1FAE5] px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Quick 2-Step Form
                </span>
              </div>

              {/* Status banner */}
              {status === 'success' && (
                <motion.div
                  role="status"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-start gap-3 p-4 mb-5 rounded-xl bg-[#D1FAE5] border border-[#6EE7B7]"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#065F46]">{statusMessage}</p>
                    <a
                      href={fastWhatsAppUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#059669] hover:underline mt-1.5"
                    >
                      Want to connect faster? Message us on WhatsApp &rarr;
                    </a>
                  </div>
                </motion.div>
              )}

              {status === 'error' && (
                <motion.div
                  role="status"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-4 mb-5 rounded-xl bg-[#FEF3C7] border border-[#FCD34D]"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <AlertCircle className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-semibold text-[#92400E]">{statusMessage}</p>
                  </div>

                  {/* 1-Click WhatsApp Fallback Button */}
                  <a
                    id="contact-form-wa-fallback-btn"
                    href={fallbackWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-white font-bold text-xs shadow transition-all duration-200 hover:brightness-105"
                    style={{ backgroundColor: '#25D366' }}
                  >
                    <WhatsAppIcon className="w-4 h-4 fill-white" />
                    <span>Send Inquiry Directly Via WhatsApp Instead &rarr;</span>
                  </a>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 1. Full Name (Required) */}
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-xs font-bold text-[#1E293B] mb-1.5 uppercase tracking-wide"
                  >
                    Full Name <span className="text-[#E11D48]">*</span>
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    placeholder="Enter your name"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E293B] placeholder-[#94A3B8] focus:outline-none focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#DBEAFE] transition-all duration-200"
                  />
                </div>

                {/* 2. Phone / WhatsApp Number (Required) */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-xs font-bold text-[#1E293B] mb-1.5 uppercase tracking-wide"
                  >
                    Phone / WhatsApp Number <span className="text-[#E11D48]">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="+971 58 XXX XXXX"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E293B] placeholder-[#94A3B8] focus:outline-none focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#DBEAFE] transition-all duration-200"
                  />
                </div>

                {/* 3. Quick-Select Chips for Popular Services */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      htmlFor="service"
                      className="block text-xs font-bold text-[#1E293B] uppercase tracking-wide"
                    >
                      Service Required
                    </label>
                    <span className="text-[11px] text-[#64748B]">Tap to select</span>
                  </div>

                  {/* Interactive Service Chips */}
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {popularServiceChips.map((chip) => {
                      const isSelected = formData.service === chip
                      return (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => handleChipSelect(chip)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                            isSelected
                              ? 'bg-[#1D4ED8] text-white shadow-sm'
                              : 'bg-[#EFF6FF] text-[#1E40AF] hover:bg-[#DBEAFE] border border-[#DBEAFE]'
                          }`}
                        >
                          {chip}
                        </button>
                      )
                    })}
                  </div>

                  {/* Dropdown for All Services */}
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    aria-label="Service Required"
                    className="w-full px-4 py-2.5 bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E293B] focus:outline-none focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#DBEAFE] transition-all duration-200"
                  >
                    <option value="">Select or choose from chips above...</option>
                    {(contactData.contact.formFields.find((f) => f.name === 'service')?.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. Email Address (Strictly Optional) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      htmlFor="email"
                      className="block text-xs font-bold text-[#1E293B] uppercase tracking-wide"
                    >
                      Email Address
                    </label>
                    <span className="text-[11px] text-[#94A3B8] font-medium">(Optional)</span>
                  </div>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E293B] placeholder-[#94A3B8] focus:outline-none focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#DBEAFE] transition-all duration-200"
                  />
                </div>

                {/* 5. Message (Strictly Optional) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      htmlFor="message"
                      className="block text-xs font-bold text-[#1E293B] uppercase tracking-wide"
                    >
                      Notes / Details
                    </label>
                    <span className="text-[11px] text-[#94A3B8] font-medium">(Optional)</span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Any specific questions or urgent turnaround requirements..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2.5 bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl text-sm text-[#1E293B] placeholder-[#94A3B8] focus:outline-none focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#DBEAFE] transition-all duration-200 resize-none"
                  />
                </div>

                {/* Submit Action Button */}
                <motion.button
                  id="contact-form-submit-btn"
                  variants={itemVariants}
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-sm disabled:opacity-70 transition-all duration-200 mt-2 shadow-lg hover:brightness-105 active:scale-98"
                  style={{
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
                    boxShadow: '0 4px 14px rgba(29,78,216,0.3)',
                  }}
                >
                  {status === 'sending' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending Your Request...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Request Free Consultation &amp; Quote
                    </>
                  )}
                </motion.button>

                <p className="text-xs text-[#94A3B8] text-center pt-1">
                  🔒 100% Confidential · No obligations · Fast same-day response
                </p>
              </form>
            </motion.div>
          </motion.div>

          {/* Contact Info & Office Details (Spans 5 cols on lg) */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? 'visible' : 'hidden'}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            {/* Direct WhatsApp Callout Card */}
            <motion.a
              variants={itemVariants}
              id="contact-side-whatsapp-card"
              href={fastWhatsAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-3xl text-white transition-all duration-300 hover:-translate-y-0.5 group shadow-lg"
              style={{
                backgroundColor: '#25D366',
                boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <WhatsAppIcon className="w-6 h-6 fill-white group-hover:scale-110 transition-transform duration-200" />
                </div>
                <div>
                  <h4 className="font-extrabold text-base leading-tight">Instant WhatsApp Assistance</h4>
                  <p className="text-white/90 text-xs">Direct with Document Advisor</p>
                </div>
              </div>
              <p className="text-white/90 text-xs mb-3 leading-relaxed">
                Connect directly on WhatsApp for quick pricing, required documents list, and urgent processing support.
              </p>
              <span className="inline-flex items-center gap-1.5 bg-black/15 px-3.5 py-1.5 rounded-full text-xs font-bold">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Online Now · Typical reply: ~5 mins
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </span>
            </motion.a>

            {/* Office Address */}
            <motion.a
              variants={itemVariants}
              href={`https://maps.google.com/?q=${encodeURIComponent(contactData.contact.officeAddressFull)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Get directions to ${contactData.contact.officeAddressFull}`}
              className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-[#DBEAFE] hover:-translate-y-0.5 transition-all duration-300 group"
              style={{ boxShadow: '0 2px 8px rgba(29,78,216,0.06)' }}
            >
              <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1D4ED8] group-hover:text-white transition-all duration-200">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wide mb-0.5">Office Location</p>
                <p className="text-sm font-bold text-[#1E293B]">{contactData.contact.officeAddressFull}</p>
                <p className="text-xs text-[#1D4ED8] mt-1 flex items-center gap-1 font-semibold">
                  <Navigation className="w-3 h-3" /> Get Directions (Al Karama, Dubai)
                </p>
              </div>
            </motion.a>

            {/* Working Hours */}
            <motion.div
              variants={itemVariants}
              className="p-5 bg-white rounded-2xl border border-[#DBEAFE]"
              style={{ boxShadow: '0 2px 8px rgba(29,78,216,0.06)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#FFFBEB] text-[#D97706] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1E293B]">Working Hours</p>
                  <p className="text-xs text-[#64748B]">Walk-ins &amp; Appointments Welcome</p>
                </div>
              </div>
              <div className="space-y-2">
                {officeHoursTable.map((row, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-xs text-[#475569]">{row.days}</span>
                    <span
                      className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                        row.status === 'open' ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#FEE2E2] text-[#E11D48]'
                      }`}
                    >
                      {row.hours}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* ===== GOOGLE MAP SECTION ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="relative rounded-3xl overflow-hidden shadow-lg"
          style={{ boxShadow: '0 20px 60px rgba(29,78,216,0.18)' }}
        >
          {/* Map Header Bar */}
          <div
            className="relative z-10 flex items-center justify-between px-6 py-4"
            style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#FCD34D]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Al Ishrat Document Clearing Service</p>
                <p className="text-blue-200 text-xs">Al Karama · Dubai, UAE · Plus Code: 68P3+9H6</p>
              </div>
            </div>
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent('Al Ishrat Document Clearing Service Al Karama Dubai')}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in Google Maps"
              className="flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm text-white rounded-xl border border-white/25 text-xs font-bold hover:bg-white/25 transition-all duration-200"
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Open Maps</span>
            </a>
          </div>

          {/* Embedded Map */}
          <div className="relative">
            <iframe
              title="Al Ishrat Document Clearing Service Location"
              src="https://maps.google.com/maps?q=Al+Karama+Dubai+UAE&output=embed&z=15"
              width="100%"
              height="340"
              style={{ border: 0, display: 'block' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Map Footer */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
            style={{ background: '#F8FBFF', borderTop: '1px solid #DBEAFE' }}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Phone className="w-3.5 h-3.5 text-[#1D4ED8]" />
                {contactData.contact.phone}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Clock className="w-3.5 h-3.5 text-[#D97706]" />
                Mon–Sat: 9:00 AM – 6:00 PM
              </div>
            </div>
            <a
              id="map-footer-whatsapp-btn"
              href={getWhatsAppUrl({ type: 'directions' })}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-xs font-bold shadow-sm"
              style={{ backgroundColor: '#25D366' }}
            >
              <WhatsAppIcon className="w-3.5 h-3.5 fill-white" />
              <span>WhatsApp for Directions</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
